import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';

import { getEnvironment } from '@/utils/environmentUtils';
import { UnreachableError } from '@/utils/errors';
import { getPosthogKey, isPosthogTrackingEnabled } from '@/utils/posthogUtils';

// Resolves once PostHog has fully loaded (including the session recording
// module). Code that touches session recording should await this before
// calling startSessionRecording / stopSessionRecording to avoid the
// "Called on script loaded before session recording is available" error.
let resolvePosthogLoaded: () => void;
export const posthogLoaded = new Promise<void>((resolve) => {
  resolvePosthogLoaded = resolve;
});

if (typeof window !== 'undefined') {
  // use a proxy to avoid ad blockers blocking PostHog
  const posthogAPIHost = (() => {
    const env = getEnvironment();
    switch (env) {
      case 'development':
      case 'staging':
        return 'https://internal-phproxy.withluminary.dev';
      case 'prod':
        return 'https://internal-phproxy.withluminary.com';
      default:
        throw new UnreachableError({
          case: env,
          message: `Unknown environment: ${env}`,
        });
    }
  })();

  posthog.init(getPosthogKey(), {
    person_profiles: 'identified_only',
    before_send: (event) => (isPosthogTrackingEnabled() ? event : null),
    // we need to to set this explicitly because we use a proxy and it will default to
    // the proxy host, which is... wrong. seems like a posthog sdk bug.
    ui_host: 'https://us.posthog.com',
    capture_pageview: false, // we do this separately in TrackPageview
    // Always start with session recording disabled. We enable it explicitly
    // in UserWatcher for tenants that allow advanced analytics.
    disable_session_recording: true,
    api_host: posthogAPIHost,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
    },
    loaded: (_posthog) => {
      resolvePosthogLoaded();
      // uncomment this to see posthog logs
      // if (getEnvironment() === 'development') {
      //   _posthog.debug();
      // }
    },
  });
}

export function PosthogProvider({ children }: { children: React.ReactNode }) {
  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}
